import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Plus, Pencil, Trash2, Check, X } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL;

const FAMILY_LABELS = {
  sales: 'Sales & Growth',
  front_of_house: 'Front-of-House / Service',
  technical_craft: 'Technical / Craft',
  management: 'Management',
};

const emptyForm = { label: '', family: 'sales', craft: false, open: false, bio: '' };

function RoleForm({ initial, onSubmit, onCancel, submitLabel }) {
  const [form, setForm] = useState(initial);

  return (
    <div className="border border-emerald-200 rounded-lg p-4 bg-emerald-50/30 space-y-3">
      <div>
        <label className="text-xs font-medium text-gray-500">Job Title</label>
        <input
          value={form.label}
          onChange={(e) => setForm({ ...form, label: e.target.value })}
          placeholder="e.g. Front Desk Executive"
          className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-gray-500">Competency Family</label>
          <select
            value={form.family}
            onChange={(e) => setForm({ ...form, family: e.target.value })}
            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            {Object.entries(FAMILY_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
          <p className="mt-1 text-[11px] text-gray-400">Decides which rubric and question bank this role is scored against.</p>
        </div>
        <div className="flex flex-col justify-center gap-2 pt-4">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={form.craft} onChange={(e) => setForm({ ...form, craft: e.target.checked })} />
            Hands-on / craft role
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={form.open} onChange={(e) => setForm({ ...form, open: e.target.checked })} />
            Currently accepting applications
          </label>
        </div>
      </div>
      <div>
        <label className="text-xs font-medium text-gray-500">Bio (what the role does, what you're looking for)</label>
        <textarea
          value={form.bio}
          onChange={(e) => setForm({ ...form, bio: e.target.value })}
          rows={3}
          className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
        />
      </div>
      <div className="flex gap-2">
        <button onClick={() => onSubmit(form)} className="flex items-center gap-1 text-xs bg-emerald-600 text-white px-4 py-2 rounded-lg">
          <Check size={14} /> {submitLabel}
        </button>
        <button onClick={onCancel} className="flex items-center gap-1 text-xs bg-gray-200 text-gray-700 px-4 py-2 rounded-lg">
          <X size={14} /> Cancel
        </button>
      </div>
    </div>
  );
}

function Roles() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/recruitment/roles`);
      setRoles(res.data.roles);
    } catch {
      toast.error('Failed to load roles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (form) => {
    if (!form.label.trim()) { toast.error('Enter a job title'); return; }
    try {
      await axios.post(`${API_URL}/api/recruitment/roles`, form);
      toast.success('Role added');
      setAdding(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add role');
    }
  };

  const update = async (id, form) => {
    try {
      await axios.put(`${API_URL}/api/recruitment/roles/${id}`, form);
      toast.success('Saved');
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    }
  };

  const toggleOpen = async (role) => {
    try {
      await axios.put(`${API_URL}/api/recruitment/roles/${role._id}`, { ...role, open: !role.open });
      load();
    } catch {
      toast.error('Failed to update');
    }
  };

  const remove = async (role) => {
    if (!window.confirm(`Delete "${role.label}" permanently? This only works if no one has applied to it yet.`)) return;
    try {
      await axios.delete(`${API_URL}/api/recruitment/roles/${role._id}`);
      toast.success('Deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete');
    }
  };

  return (
    <div>
      <p className="text-xs text-gray-500 mb-4">
        Every job listed on the hiring site, live and behind-the-scenes. Toggle a role open to start
        accepting applications, or closed to hide the Apply button while still showing it on the site.
        Roles with real applications against them can be closed but not deleted, so history stays intact.
      </p>

      {loading ? <p className="text-sm text-gray-400">Loading...</p> : (
        <div className="space-y-3">
          {roles.map((role) => (
            <div key={role._id}>
              {editingId === role._id ? (
                <RoleForm
                  initial={{ label: role.label, family: role.family, craft: role.craft, open: role.open, bio: role.bio }}
                  onSubmit={(form) => update(role._id, form)}
                  onCancel={() => setEditingId(null)}
                  submitLabel="Save"
                />
              ) : (
                <div className="border border-gray-200 rounded-lg p-4 bg-white flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-gray-800">{role.label}</p>
                      <span className="text-[10px] uppercase tracking-wide bg-gray-100 text-gray-500 px-2 py-0.5 rounded">{FAMILY_LABELS[role.family]}</span>
                      {role.craft && <span className="text-[10px] uppercase tracking-wide bg-purple-100 text-purple-600 px-2 py-0.5 rounded">Craft</span>}
                      <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded ${role.open ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                        {role.open ? 'Accepting Applications' : 'Not Recruiting'}
                      </span>
                    </div>
                    {role.bio && <p className="mt-1.5 text-xs text-gray-500 leading-relaxed">{role.bio}</p>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => toggleOpen(role)}
                      className={`text-xs px-2.5 py-1.5 rounded-lg font-medium ${role.open ? 'bg-gray-100 text-gray-600 hover:bg-gray-200' : 'bg-emerald-600 text-white hover:bg-emerald-700'}`}
                    >
                      {role.open ? 'Close' : 'Open'}
                    </button>
                    <button onClick={() => setEditingId(role._id)} className="p-1.5 text-gray-400 hover:text-emerald-600" title="Edit"><Pencil size={15} /></button>
                    <button onClick={() => remove(role)} className="p-1.5 text-gray-400 hover:text-red-600" title="Delete"><Trash2 size={15} /></button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="mt-4">
        {adding ? (
          <RoleForm initial={emptyForm} onSubmit={create} onCancel={() => setAdding(false)} submitLabel="Add Role" />
        ) : (
          <button onClick={() => setAdding(true)} className="flex items-center gap-1 text-sm text-emerald-700 hover:text-emerald-800">
            <Plus size={16} /> Add New Role
          </button>
        )}
      </div>
    </div>
  );
}

export default Roles;
